import React, { useState } from "react";
import { Spin } from "antd";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import ReportingLogDataList from "./ReportingLogDataList";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { getFullName } from "../../careprovider/visits/utils";

const LOADER_STYLE = {
  display: "flex",
  justifyContent: "center",
  minHeight: 120,
  alignItems: "center",
};

const PATIENT_REPORTING_TABLE_KEY = "patientReporting";

const safeCellValue = (val) =>
  val == null || val === "" ? "" : String(val).trim();

/**
 * Modal that shows a loader while fetching, then the entity list (patients or workforce).
 */
const ReportingLogCellModal = ({
  open,
  onClose,
  loading,
  data,
  tableKey,
  exportContext = {},
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const isPatientData = tableKey === PATIENT_REPORTING_TABLE_KEY;

  const handleExport = async () => {
    if (!data?.length) return;
    setExportLoading(true);
    try {
      const {
        year = "",
        monthLabel = "",
        quarterLabel = "",
        categoryName = "",
        tableName = "",
      } = exportContext;

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Report details", {
        views: [{ rightToLeft: false }],
      });

      let rowIndex = 1;
      if (monthLabel) {
        sheet.getCell(rowIndex, 1).value = "Month";
        const monthRow = sheet.getRow(rowIndex);
        monthRow.font = { bold: true };
        sheet.getCell(rowIndex, 2).value = monthLabel;
        rowIndex++;
      }
      if (quarterLabel) {
        sheet.getCell(rowIndex, 1).value = "Quarter";
        const quarterRow = sheet.getRow(rowIndex);
        quarterRow.font = { bold: true };
        sheet.getCell(rowIndex, 2).value = quarterLabel;
        rowIndex++;
      }
      if (year) {
        sheet.getCell(rowIndex, 1).value = "Year";
        const yearRow = sheet.getRow(rowIndex);
        yearRow.font = { bold: true };
        sheet.getCell(rowIndex, 2).value = year;
        rowIndex++;
      }
      if (categoryName) {
        sheet.getCell(rowIndex, 1).value = "Category";
        const categoryRow = sheet.getRow(rowIndex);
        categoryRow.font = { bold: true };
        sheet.getCell(rowIndex, 2).value = categoryName;
        rowIndex++;
      }
      if (tableName) {
        sheet.getCell(rowIndex, 1).value = "Table";
        const tableRow = sheet.getRow(rowIndex);
        tableRow.font = { bold: true };
        sheet.getCell(rowIndex, 2).value = tableName;
        rowIndex++;
      }
      rowIndex += 1;

      const baseHeaders = ["Name", "Email", "Phone"];
      const headers = isPatientData
        ? ["Name", "Email", "Phone", "MRN"]
        : baseHeaders;
      const headerRow = sheet.getRow(rowIndex);
      headerRow.values = [null, null, ...headers];
      headerRow.font = { bold: true };
      rowIndex++;

      data.forEach((item) => {
        const fullName = getFullName(item?.givenName, item?.familyName) || "—";
        const row = sheet.getRow(rowIndex);
        const rowValues = [
          safeCellValue(fullName),
          safeCellValue(item?.email),
          safeCellValue(item?.telephone ?? item?.phone),
        ];
        if (isPatientData) {
          const mrn = item?.medicalRecordNumber ?? "";
          rowValues.push(safeCellValue(mrn));
        }
        row.values = [null, null, ...rowValues];
        // if (isPatientData && rowValues[3]) {
        //   row.getCell(5).numFmt = "@";
        // }
        rowIndex++;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 10);
      saveAs(blob, `Report-Details-${timestamp}.xlsx`);
    } finally {
      setExportLoading(false);
    }
  };

  const footerButtons = (
    <>
      <SecondaryButton
        buttonText="Close"
        handleOnClick={() => onClose(false)}
      />
      <PrimaryButton
        buttonText="Export"
        handleOnClick={handleExport}
        loading={exportLoading}
      />
    </>
  );

  return (
    <CustomModal
      isModalOpen={open}
      closeModal={onClose}
      title="Report details"
      footer={footerButtons}
    >
      {loading ? (
        <div style={LOADER_STYLE}>
          <Spin size="large" />
        </div>
      ) : (
        <ReportingLogDataList cellModalData={data} tableKey={tableKey} />
      )}
    </CustomModal>
  );
};

export default ReportingLogCellModal;
